/**
 * Checkout Page for Subscriptions
 * Allows users to create or upgrade their subscription
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { PLANS } from '@/data/plans';

export function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { invalidate: invalidateSubscriptionStore } = useSubscriptionStore();
  const { plans: apiPlans, currentSubscription, loading, error, loadPlans, loadCurrentSubscription, createSubscription, upgradePlan, downgradePlan, clearError, validateCoupon } = useSubscription();
  
  const [selectedPlan, setSelectedPlan] = useState<string | null>(searchParams.get('plan'));
  const [couponCode, setCouponCode] = useState('');
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [couponError, setCouponError] = useState('');
  const checkoutSummaryRef = useRef<HTMLDivElement>(null);

  // Scroll to summary when a plan is selected
  useEffect(() => {
    if (selectedPlan && checkoutSummaryRef.current) {
      setTimeout(() => {
        checkoutSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?mode=login&redirect=/checkout');
      return;
    }

    loadPlans();
    loadCurrentSubscription();
  }, [isAuthenticated, loadPlans, loadCurrentSubscription, navigate]);

  const handleSelectPlan = async (planCode: string) => {
    if (!isAuthenticated) {
      navigate(`/auth?mode=login&redirect=/checkout?plan=${planCode}`);
      return;
    }

    setCouponError('');
    setProcessingPlan(planCode);

    try {
      const hasActiveSub = !!currentSubscription && currentSubscription.status === 'active';

      if (planCode === 'free') {
        // Plano free não requer pagamento
        if (hasActiveSub) {
          await downgradePlan(planCode, couponCode || undefined);
        } else {
          await createSubscription(planCode, couponCode || undefined);
        }
        invalidateSubscriptionStore();
        navigate('/subscription-dashboard');
        return;
      }

      // Se há cupom, valida antes de decidir o caminho
      if (couponCode) {
        const coupon = await validateCoupon(couponCode, planCode);

        if (!coupon.valid) {
          setCouponError(coupon.message || 'Cupom inválido.');
          return;
        }

        // Cupom de 100% — finaliza sem passar pelo pagamento
        if (coupon.discount_type === 'percentage' && coupon.discount_value === 100) {
          if (hasActiveSub) {
            await upgradePlan(planCode, couponCode);
          } else {
            await createSubscription(planCode, couponCode);
          }
          invalidateSubscriptionStore();
          navigate('/subscription-dashboard');
          return;
        }
      }

      const params = new URLSearchParams({ plan: planCode });
      if (couponCode) params.set('coupon', couponCode);
      navigate(`/checkout/payment?${params.toString()}`);
    } catch {
      // erro exibido via error do hook
    } finally {
      setProcessingPlan(null);
    }
  };

  const selectedPlanData = selectedPlan ? PLANS[selectedPlan as keyof typeof PLANS] : null;
  const selectedPlanPrice = selectedPlan 
    ? apiPlans.find((p) => p.code === selectedPlan)?.price_cents ?? 0
    : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />

      <div className="pt-32 pb-20 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1
              className="text-5xl md:text-6xl font-light text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Escolha seu <strong>plano</strong>
            </h1>
            <p className="text-white/40 text-base max-w-md mx-auto">
              Comece gratuitamente e escale conforme seu ministério cresce.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-8">
              <p className="text-sm text-red-400">{error}</p>
              <button
                onClick={clearError}
                className="text-xs text-red-400 hover:text-red-300 mt-2"
              >
                Descartar
              </button>
            </div>
          )}

          {/* Checkout Form - if plan selected */}
          {selectedPlan && selectedPlanData && (
            <div ref={checkoutSummaryRef} className="bg-[#111111] border border-white/5 rounded-2xl p-8 mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  Resumo do Pedido
                </h2>
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="text-white/50 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4 pb-6 border-b border-white/5">
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Plano:</span>
                  <span className="text-white font-semibold">{selectedPlanData.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Preço:</span>
                  <span className="text-white font-semibold">
                    {selectedPlanPrice === 0
                      ? 'Grátis'
                      : `R$ ${(selectedPlanPrice / 100).toFixed(2).replace('.', ',')}/mês`}
                  </span>
                </div>
              </div>

              {/* Coupon Input */}
              <div className="mt-6">
                <label className="block text-sm text-white/70 mb-2">
                  Código de Cupom (opcional)
                </label>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                  placeholder="Digite seu cupom"
                  className={`w-full bg-[#0A0A0F] border rounded-lg px-4 py-2 text-white placeholder-white/30 focus:border-[#C8A96E] outline-none transition-all ${couponError ? 'border-red-500/60' : 'border-white/10'}`}
                />
                {couponError && (
                  <p className="text-red-400 text-xs mt-1">{couponError}</p>
                )}
              </div>

              {/* CTA */}
              <button
                onClick={() => handleSelectPlan(selectedPlan)}
                disabled={processingPlan === selectedPlan || loading}
                className="w-full mt-8 py-3 px-4 bg-[#C8A96E] text-[#0A0A0F] rounded-lg font-semibold hover:bg-[#D4B882] disabled:opacity-50 transition-all"
              >
                {processingPlan === selectedPlan ? 'Processando...' : 'Continuar'}
              </button>
            </div>
          )}

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full text-center py-12">
                <p className="text-white/50">Carregando planos...</p>
              </div>
            ) : apiPlans.length > 0 ? (
              Object.values(PLANS).map((planData) => {
                const apiPlan = apiPlans.find((p) => p.code === planData.code);
                const priceCents = apiPlan?.price_cents ?? 0;

                return (
                  <CheckoutPlanCard
                    key={planData.code}
                    plan={planData}
                    priceCents={priceCents}
                    isSelected={selectedPlan === planData.code}
                    isLoading={processingPlan === planData.code}
                    onSelect={handleSelectPlan}
                  />
                );
              })
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-white/50">Nenhum plano disponível</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

interface CheckoutPlanCardProps {
  plan: (typeof PLANS)[keyof typeof PLANS];
  priceCents: number;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: (planCode: string) => void;
}

const CheckoutPlanCard = ({ plan, priceCents, isSelected, isLoading, onSelect }: CheckoutPlanCardProps) => {
  const displayPrice = priceCents === 0 ? 'Grátis' : `R$ ${(priceCents / 100).toFixed(2).replace('.', ',')}`;

  return (
    <div
      className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 cursor-pointer ${
        plan.highlight
          ? 'bg-[#C8A96E] text-[#0A0A0F]'
          : 'bg-[#111111] border border-white/5 text-white hover:border-white/15'
      } ${isSelected ? 'ring-2 ring-[#C8A96E]' : ''}`}
    >
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0A0A0F] text-[#C8A96E] text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest whitespace-nowrap">
          Mais popular
        </div>
      )}

      <div className="mb-6">
        <p className={`text-xs uppercase tracking-widest mb-3 ${plan.highlight ? 'text-[#0A0A0F]/60' : 'text-white/40'}`}>
          {plan.name}
        </p>
        <div className="flex items-end gap-2 mb-2">
          <span className="text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            {displayPrice}
          </span>
          {priceCents > 0 && (
            <span className={`text-sm mb-1 ${plan.highlight ? 'text-[#0A0A0F]/60' : 'text-white/40'}`}>
              {plan.period}
            </span>
          )}
        </div>
        <p className={`text-sm leading-relaxed ${plan.highlight ? 'text-[#0A0A0F]/70' : 'text-white/40'}`}>
          {plan.description}
        </p>
      </div>

      <div className="flex-1 mb-8">
        <div className="flex flex-col gap-3">
          {plan.features.map((f) => (
            <div key={f} className="flex items-center gap-3">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-check-line text-sm ${plan.highlight ? 'text-[#0A0A0F]' : 'text-[#C8A96E]'}`}></i>
              </div>
              <span className={`text-sm ${plan.highlight ? 'text-[#0A0A0F]' : 'text-white/70'}`}>{f}</span>
            </div>
          ))}
          {plan.disabled.map((f) => (
            <div key={f} className="flex items-center gap-3 opacity-40">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-close-line text-sm"></i>
              </div>
              <span className="text-sm line-through">{f}</span>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => onSelect(plan.code)}
        disabled={isLoading}
        className={`w-full py-3 rounded-full text-sm font-semibold text-center transition-all duration-300 whitespace-nowrap ${
          isSelected
            ? plan.highlight
              ? 'bg-[#0A0A0F] text-white opacity-75'
              : 'bg-[#C8A96E] text-[#0A0A0F]'
            : plan.highlight
              ? 'bg-[#0A0A0F] text-white hover:bg-[#1A1A1A]'
              : 'border border-white/20 text-white hover:border-white/40 hover:bg-white/5'
        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isLoading ? 'Processando...' : `Selecionar ${plan.name}`}
      </button>
    </div>
  );
};
